import { useState } from 'react'
import { sileo } from 'sileo'

/**
 * Componente de formulario para la edición de una prioridad existente.
 * * * Permite modificar el nombre y el nivel de criticidad de una prioridad.
 * * **Características**:
 * - Valida campos obligatorios y rangos numéricos.
 * - Realiza una verificación de cambios para evitar peticiones innecesarias.
 * - Utiliza `sileo` para el feedback visual (alertas y errores).
 * - Maneja el estado de carga (`loading`) durante la persistencia asíncrona.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.priority - Objeto con los datos actuales de la prioridad.
 * @param {number|string} props.priority.id - Identificador único de la prioridad.
 * @param {string} props.priority.name - Nombre actual de la prioridad.
 * @param {number} props.priority.level - Nivel de criticidad actual.
 * @param {Function} props.onSave - Función asíncrona para guardar los cambios.
 * Recibe (id, payload).
 * * @returns {JSX.Element} Un formulario estilizado con validaciones y gestión de estados.
 */
const EditPriority = ({ priority, onSave }) => {
  /** @type {[string, Function]} Estado local para el nombre editado */
  const [name, setName] = useState(priority.name)

  /** @type {[number|string, Function]} Estado local para el nivel editado */
  const [level, setLevel] = useState(priority.level)

  /** @type {[boolean, Function]} Estado para el spinner de carga y deshabilitar el botón */
  const [loading, setLoading] = useState(false)

  /**
   * Procesa el envío del formulario.
   * * Realiza validaciones de contenido, verifica si existen cambios reales respecto
   * a los datos originales y ejecuta el callback `onSave`.
   * * @param {React.FormEvent} e - Evento de envío nativo.
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validación: Nombre no vacío
    if (!name.trim()) {
      return sileo.warning({
        title: 'Campo requerido',
        description: 'El nombre es obligatorio.',
      })
    }

    // Validación: Nivel lógico
    if (!level || level < 1) {
      return sileo.warning({
        title: 'Nivel inválido',
        description: 'El nivel debe ser mayor a 0.',
      })
    }

    // Dirty checking: ¿Hubo cambios reales?
    const noChanges =
      name.trim() === priority.name && Number(level) === priority.level

    if (noChanges) {
      return sileo.info({
        title: 'Sin cambios',
        description: 'No modificaste nada.',
      })
    }

    try {
      setLoading(true)

      // Ejecución de la lógica de persistencia externa
      await onSave(priority.id, {
        name: name.trim(),
        level: Number(level),
      })
    } catch (err) {
      console.error(err)
      sileo.error({
        title: 'Error',
        description: 'No se pudo guardar.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="pf">
      <div className="pf-row">
        <div className="pf-field pf-grow">
          <label className="pf-label" htmlFor="edit-priority-name">
            Nombre
          </label>
          <input
            id="edit-priority-name"
            type="text"
            className="form-control form-control-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="pf-field" style={{ width: 110 }}>
          <label className="pf-label" htmlFor="edit-priority-level">
            Nivel
          </label>
          <input
            id="edit-priority-level"
            type="number"
            className="form-control form-control-sm"
            value={level}
            min="1"
            onChange={(e) => setLevel(e.target.value)}
          />
        </div>
      </div>

      <div className="pf-hint">A mayor número, mayor prioridad.</div>

      <div className="pf-actions">
        <button
          type="submit"
          className="btn btn-sm btn-primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="fa-solid fa-floppy-disk me-2" />
              Guardar cambios
            </>
          )}
        </button>
      </div>

      <style>{`
        .pf { display: flex; flex-direction: column; gap: 10px; }
        .pf-row { display: flex; gap: 10px; }
        .pf-field { display: flex; flex-direction: column; gap: 4px; }
        .pf-grow { flex: 1; min-width: 0; }
        .pf-label {
          margin: 0; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.04em;
          text-transform: uppercase; color: var(--gm-text-muted);
        }
        .pf-hint { font-size: 0.75rem; color: var(--gm-text-muted); }
        .pf-actions { display: flex; justify-content: flex-end; gap: 6px; }
      `}</style>
    </form>
  )
}

export default EditPriority
